import React, { forwardRef } from 'react'
import { Button as MuiButton, ButtonProps as MuiButtonProps } from '@mui/material'
import { styled } from '@mui/material/styles'
import { pixelToRemMap } from '@zuto/theme/src/mui/Typography'
import { SvgIcon } from '@mui/material'

export interface ZutoButtonProps extends MuiButtonProps {
  subTextLineOne?: string
  subText?: string
  startSvgIcon?: React.ElementType
  endSvgIcon?: React.ElementType
  disabled?: boolean
  hideNext?: boolean
}

const TextWrapper = styled('span')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center', // aligns children like SubText and main text
  justifyContent: 'center',
  textAlign: 'center'
}))

const SubText = styled('span')(() => ({
  fontSize: pixelToRemMap[14],
  lineHeight: pixelToRemMap[20],
  marginBottom: `2px`,
  opacity: 1,
  display: "flex",
  justifyContent: 'center',
  alignItems: 'center'
}))

const Button = forwardRef<HTMLButtonElement, ZutoButtonProps>(({
  children,
  subTextLineOne,
  subText,
  startSvgIcon,
  endSvgIcon,
  size = 'large',
  variant = 'contained',
  color = "primary",
  ...props
}, ref) => {
  const showSubText = (subText || subTextLineOne)

  // icons are designed for a 24x24px viewport but we need to add some spacing
  // 24 + 16 = 40, the minus margin is to compensate and recentre the content
  const StartSvgIcon = startSvgIcon && <SvgIcon component={startSvgIcon} sx={{ width: 40, marginLeft: '-16px' }} />
  const EndSvgIcon = endSvgIcon && <SvgIcon component={endSvgIcon} sx={{ width: 40, marginRight: '-16px' }} />

  return (
    <MuiButton
      disableRipple
      startIcon={startSvgIcon ? StartSvgIcon : null}
      endIcon={endSvgIcon ? EndSvgIcon : null}
      ref={ref}
      size={size}
      color={color}
      variant={variant}
      {...props}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '& .MuiButton-startIcon': {
          marginLeft: '-30px'
        },
        '& .MuiButton-endIcon': {
          marginRight: '-30px'
        },
        ...props.sx
      }}
    >
      <TextWrapper>
        {children}
        {showSubText && (
          <span data-testid="button-subtext">
            {subTextLineOne && <SubText data-testid="button-subtext">{subTextLineOne}</SubText>}
            {subText && <SubText sx={{ fontWeight: 400 }}>{subText}</SubText>}
          </span>
        )}
      </TextWrapper>
    </MuiButton>
  )
})

export default Button